import React, { useEffect, useMemo, useState } from 'react';
import Tooltip from './common/Tooltip';

// Retain trusted server-clock offsets across cached stock-page remounts.
// The bounded, in-memory cache contains only timestamps, not portfolio data.
const clockAnchors = new Map();
const makeAnchor = (asOf) => ({ asOf, epoch: Date.parse(asOf), wall: Date.now() });
const rememberAnchor = (anchor) => {
  clockAnchors.set(anchor.asOf, anchor);
  if (clockAnchors.size > 64) clockAnchors.delete(clockAnchors.keys().next().value);
};
const sameLocalDay = (a, b) => a.getFullYear() === b.getFullYear()
  && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const formatDuration = (milliseconds) => {
  const minutes = Math.max(1, Math.ceil(milliseconds / 60000));
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return hours ? `${hours}h${remainder ? ` ${remainder}min` : ''}` : `${minutes}min`;
};

const formatOpening = (instant, now) => {
  const date = new Date(instant);
  if (sameLocalDay(date, new Date(now))) return `in ${formatDuration(instant - now)}`;
  const day = new Intl.DateTimeFormat('en-GB', {
    weekday: 'long', day: 'numeric', month: 'short',
    ...(date.getFullYear() !== new Date(now).getFullYear() ? { year: 'numeric' } : {}),
  }).format(date);
  const time = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZoneName: 'short',
  }).format(date);
  return `${day} at ${time}`;
};

// All session instants come from the backend's listing-exchange calendar.
// The local timer only updates presentation; it makes no API/provider requests.
const StockMarketHours = ({ marketHours, exchange, isFresh = true }) => {
  const [, setTick] = useState(0);
  const [anchor, setAnchor] = useState(() => {
    const asOf = marketHours?.asOf;
    if (isFresh && Number.isFinite(Date.parse(asOf))) return makeAnchor(asOf);
    return clockAnchors.get(asOf) || null;
  });
  useEffect(() => {
    const asOf = marketHours?.asOf;
    if (!isFresh || !Number.isFinite(Date.parse(asOf))) return;
    const trusted = clockAnchors.get(asOf)
      || (anchor?.asOf === asOf ? anchor : makeAnchor(asOf));
    rememberAnchor(trusted);
    setAnchor(trusted);
  }, [marketHours?.asOf, isFresh, anchor]);
  // Wall-clock ELAPSED time advances through OS sleep; the server offset is
  // retained. A cached mount without a trusted anchor must not guess the time.
  const elapsed = anchor ? Date.now() - anchor.wall : -1;
  const now = elapsed >= 0 ? anchor.epoch + elapsed : null;
  const sessions = useMemo(() => (marketHours?.sessions || [])
    .map((session) => ({ open: Date.parse(session.opensAt), close: Date.parse(session.closesAt) }))
    .filter((session) => Number.isFinite(session.open) && Number.isFinite(session.close)
      && session.open < session.close)
    .sort((a, b) => a.open - b.open), [marketHours?.sessions]);
  const validUntil = Date.parse(marketHours?.validUntil);

  useEffect(() => {
    const refresh = () => setTick((value) => value + 1);
    const interval = setInterval(refresh, 60000);
    const onVisibility = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', refresh);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', refresh);
    };
  }, []);

  // Also redraw at exact open/close/expiry boundaries, not a minute afterwards.
  const nextBoundary = now == null ? Infinity : Math.min(...sessions.flatMap(({ open, close }) => [open, close])
    .concat(Number.isFinite(validUntil) ? [validUntil] : []).filter((instant) => instant > now));
  useEffect(() => {
    if (!Number.isFinite(nextBoundary)) return undefined;
    const timeout = setTimeout(() => setTick((value) => value + 1),
      Math.min(2147483647, Math.max(1, nextBoundary - now)));
    return () => clearTimeout(timeout);
  }, [nextBoundary, now]);

  if (!marketHours) return null;
  const name = marketHours.exchangeName || exchange || marketHours.mic || 'Exchange';
  const available = now != null && ['OPEN', 'CLOSED'].includes(marketHours.status) && sessions.length > 0
    && Number.isFinite(validUntil) && now < validUntil;
  const current = available ? sessions.find(({ open, close }) => open <= now && now < close) : null;
  const next = available ? sessions.find(({ open }) => open > now) : null;
  const status = available ? (current ? 'open' : 'closed') : 'hours unavailable';
  const transition = current ? `Closes in ${formatDuration(current.close - now)}`
    : next ? `Opens ${formatOpening(next.open, now)}` : '';
  const localZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const explanation = available
    ? `Scheduled regular trading hours (${marketHours.timeZone || marketHours.mic}). Opening dates/times are shown in your local time (${localZone}). Includes scheduled holidays and shortened sessions; not a live trading-halt feed.`
    : 'The exchange calendar or a trusted server clock is unavailable, or the schedule has expired. No opening time is guessed.';

  return (
    <Tooltip title={explanation} position="bottom" trigger="mouseenter focus click" interactive={false} arrow theme="dark" size="small">
      <span className="inline-flex max-w-full flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-gray-400" tabIndex={0}>
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className={`h-1.5 w-1.5 shrink-0 rounded-full ${current ? 'bg-green-500' : 'bg-gray-400 dark:bg-gray-500'}`} />
          <span className={current ? 'text-green-600 dark:text-green-400' : ''}>{name} {status}</span>
        </span>
        {transition && <span>· {transition}</span>}
      </span>
    </Tooltip>
  );
};

export default StockMarketHours;
